import React, { useState, useEffect } from 'react';
import { groupApi } from '../services/groupApi';
import Avatar from './Avatar';
import {
  IoSearchOutline,
  IoCreateOutline,
  IoTrashOutline,
  IoShieldOutline,
  IoChevronDownOutline,
  IoChevronForwardOutline
} from 'react-icons/io5';
import './AdminDashboard.css';

function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const response = await groupApi.getGroups();
      if (response.success) {
        setGroups(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleEditPermissions = (group) => {
    setEditingGroup(group);
    setPermissions(group.permissions || []);
  };

  const handleSavePermissions = async () => {
    setEditingGroup(null);
  };

  const toggleExpand = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(search.toLowerCase()) ||
    group.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-users">
      <div className="admin-users-header">
        <div className="admin-search-box">
          <IoSearchOutline />
          <input
            type="text"
            placeholder="Search groups..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}></th>
              <th>Group</th>
              <th>Members</th>
              <th>Permissions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="admin-spinner"></div>
                </td>
              </tr>
            ) : filteredGroups.length === 0 ? (
              <tr>
                <td colSpan="6">
                  <div className="admin-empty-state">
                    <IoShieldOutline className="admin-empty-icon" />
                    <div className="admin-empty-title">No groups found</div>
                    <div className="admin-empty-text">Groups will appear here when created.</div>
                  </div>
                </td>
              </tr>
            ) : (
              filteredGroups.map(group => (
                <React.Fragment key={group.id}>
                  <tr className="admin-group-row">
                    <td>
                      <button
                        className="admin-expand-btn"
                        onClick={() => toggleExpand(group.id)}
                      >
                        {expandedGroups[group.id] ? <IoChevronDownOutline /> : <IoChevronForwardOutline />}
                      </button>
                    </td>
                    <td>
                      <div className="admin-table-user">
                        <div className="admin-group-icon">
                          <IoShieldOutline />
                        </div>
                        <div>
                          <div className="admin-table-user-name">
                            {group.name}
                            {group.is_system && <span className="admin-system-badge">System</span>}
                          </div>
                          <div className="admin-table-user-email">{group.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="admin-member-count">{group.members?.length || 0}</span>
                    </td>
                    <td>
                      <div className="admin-groups-permissions">
                        {group.permissions?.slice(0, 3).map((perm, i) => (
                          <span key={i} className="admin-permission-tag">{perm}</span>
                        ))}
                        {group.permissions?.length > 3 && (
                          <span className="admin-permission-tag">+{group.permissions.length - 3}</span>
                        )}
                        {(!group.permissions || group.permissions.length === 0) && (
                          <span className="admin-permission-tag">None</span>
                        )}
                      </div>
                    </td>
                    <td>{formatDate(group.created_at)}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="admin-action-btn"
                          title="Manage Permissions"
                          onClick={() => handleEditPermissions(group)}
                        >
                          <IoCreateOutline />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedGroups[group.id] && group.members && group.members.length > 0 && (
                    <tr className="admin-group-members-row">
                      <td colSpan="6">
                        <div className="admin-group-members">
                          <div className="admin-group-members-title">Members:</div>
                          <div className="admin-group-members-list">
                            {group.members.map((member, idx) => (
                              <div key={idx} className="admin-group-member">
                                {member.user ? (
                                  <>
                                    <Avatar user={member.user} size="small" />
                                    <span>{member.user.display_name || member.user.username}</span>
                                    <span className="admin-member-role">({member.role})</span>
                                  </>
                                ) : (
                                  <span>User ID: {member.user_id}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Permission Editor Modal */}
      {editingGroup && (
        <div className="admin-modal-overlay" onClick={() => setEditingGroup(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Permissions - {editingGroup.name}</h3>
              <button className="admin-modal-close" onClick={() => setEditingGroup(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-permissions-editor">
                <label className="admin-permission-checkbox">
                  <input
                    type="checkbox"
                    checked={permissions.includes('dashboard')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPermissions([...permissions, 'dashboard']);
                      } else {
                        setPermissions(permissions.filter(p => p !== 'dashboard'));
                      }
                    }}
                  />
                  <span>Access Dashboard</span>
                </label>
                <label className="admin-permission-checkbox">
                  <input
                    type="checkbox"
                    checked={permissions.includes('manage_users')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPermissions([...permissions, 'manage_users']);
                      } else {
                        setPermissions(permissions.filter(p => p !== 'manage_users'));
                      }
                    }}
                  />
                  <span>Manage Users</span>
                </label>
                <label className="admin-permission-checkbox">
                  <input
                    type="checkbox"
                    checked={permissions.includes('manage_groups')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPermissions([...permissions, 'manage_groups']);
                      } else {
                        setPermissions(permissions.filter(p => p !== 'manage_groups'));
                      }
                    }}
                  />
                  <span>Manage Groups</span>
                </label>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setEditingGroup(null)}>Cancel</button>
              <button className="admin-btn primary" onClick={handleSavePermissions}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGroups;
